import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, Lightning, FilmStrip, ArrowLeft, ArrowRight, DownloadSimple, ShareNetwork, Eye, Repeat, X, Play, Pause, Users, QrCode, Check, Trophy, Fire, Star, Copy, LinkSimple } from '@phosphor-icons/react';
import { io } from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import FaceMeshOverlay from '../components/FaceMeshOverlay';
import ArShareCard from '../components/ArShareCard';
import MemoryBookMessage from '../components/MemoryBookMessage';
import MissionNudge from '../components/MissionNudge';
import BackButton from '../components/BackButton';
import { useBoothCameraPipeline } from '../hooks/useBoothCameraPipeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

// Shared gallery store (localStorage)
const GALLERY_KEY = (slug) => `pocket_gallery_${slug}`;
const saveToStore = (slug, item) => {
  const key = GALLERY_KEY(slug);
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  existing.unshift(item);
  if (existing.length > 50) existing.length = 50;
  localStorage.setItem(key, JSON.stringify(existing));
};
const getStore = (slug) => JSON.parse(localStorage.getItem(GALLERY_KEY(slug)) || '[]');

// Challenge Engine
const CHALLENGES_KEY = (slug) => `pocket_challenges_${slug}`;
const getChallengeProgress = (slug) => JSON.parse(localStorage.getItem(CHALLENGES_KEY(slug)) || '{}');
const saveChallengeProgress = (slug, data) => localStorage.setItem(CHALLENGES_KEY(slug), JSON.stringify(data));

const CHALLENGES = [
  // Funny / Chaotic (10)
  { id: 'worst_dance', name: 'Worst Dance Move', desc: 'Do your worst dance move', icon: '💃', target: 1, cat: 'funny' },
  { id: 'funny_face', name: 'Funniest Face', desc: 'Pull your funniest face', icon: '😝', target: 1, cat: 'funny' },
  { id: 'movie_scene', name: 'Movie Scene', desc: 'Recreate a famous movie scene', icon: '🎬', target: 1, cat: 'funny' },
  { id: 'slow_motion', name: 'Slow Motion', desc: 'Act like you are in slow motion', icon: '🐢', target: 1, cat: 'funny' },
  { id: 'famous', name: 'Famous', desc: 'Pretend you are famous', icon: '📸', target: 1, cat: 'funny' },
  { id: 'fake_laugh', name: 'Fake Laugh', desc: 'Fake laugh for 3 seconds', icon: '😂', target: 1, cat: 'funny' },
  { id: 'model_pose', name: 'Model Pose', desc: 'Strike your best model pose', icon: '💅', target: 1, cat: 'funny' },
  { id: 'impression', name: 'Impression', desc: 'Do your best impression of anyone', icon: '🎭', target: 1, cat: 'funny' },
  { id: 'statue', name: 'Freeze', desc: 'Freeze like a statue for 5 seconds', icon: '🗿', target: 1, cat: 'funny' },
  { id: 'lottery', name: 'Lottery Winner', desc: 'Act like you just won the lottery', icon: '💸', target: 1, cat: 'funny' },
  // Emotional / Meaningful (10)
  { id: 'advice', name: 'Advice', desc: 'Give advice to the couple', icon: '💍', target: 1, cat: 'emotional' },
  { id: 'ten_years', name: 'Future Vision', desc: 'Where will they be in 10 years?', icon: '🔮', target: 1, cat: 'emotional' },
  { id: 'fav_memory', name: 'Favourite Memory', desc: 'Share your favourite memory with them', icon: '🥹', target: 1, cat: 'emotional' },
  { id: 'kind_words', name: 'Kind Words', desc: 'Say something kind about the couple', icon: '❤️', target: 1, cat: 'emotional' },
  { id: 'one_word', name: 'One Word', desc: 'Describe them in one word', icon: '🧠', target: 1, cat: 'emotional' },
  { id: 'future_msg', name: 'Future Message', desc: 'Record a message for their future', icon: '🎥', target: 1, cat: 'emotional' },
  { id: 'perfect', name: 'Perfect Together', desc: 'What makes them perfect together?', icon: '💕', target: 1, cat: 'emotional' },
  { id: 'first_impression', name: 'First Impression', desc: 'Your first impression of them', icon: '👀', target: 1, cat: 'emotional' },
  { id: 'wish', name: 'Best Wish', desc: 'What do you wish them most?', icon: '🌟', target: 1, cat: 'emotional' },
  { id: 'love_is', name: 'Love Is...', desc: 'Finish this sentence: Love is...', icon: '💌', target: 1, cat: 'emotional' },
  // Social / Interactive (10)
  { id: 'stranger_photo', name: 'New Friends', desc: 'Photo with someone you just met', icon: '🤝', target: 1, cat: 'social' },
  { id: 'oldest_guest', name: 'Elder', desc: 'Find the oldest guest for a photo', icon: '👵', target: 1, cat: 'social' },
  { id: 'best_dressed', name: 'Best Dressed', desc: 'Find the best dressed guest', icon: '👗', target: 1, cat: 'social' },
  { id: 'table_shot', name: 'Table Shot', desc: 'Get your whole table in one shot', icon: '🍽', target: 1, cat: 'social' },
  { id: 'couple_photo', name: 'With The Couple', desc: 'Take a photo with the couple', icon: '💑', target: 1, cat: 'social' },
  { id: 'same_name', name: 'Same Name', desc: 'Find someone with the same name', icon: '🔍', target: 1, cat: 'social' },
  { id: 'stranger_selfie', name: 'Stranger Selfie', desc: 'Take a selfie with a stranger', icon: '🤳', target: 1, cat: 'social' },
  { id: 'swap_item', name: 'Swap', desc: 'Swap an item of clothing with someone', icon: '🧢', target: 1, cat: 'social' },
  { id: 'copy_pose', name: 'Copy Pose', desc: 'Recreate someone else\'s pose exactly', icon: '🔁', target: 1, cat: 'social' },
  { id: 'three_gens', name: '3 Generations', desc: 'Get 3 generations in one shot', icon: '👨‍👩‍👧‍👦', target: 1, cat: 'social' },
  // Energy / Party (10)
  { id: 'wildest_dance', name: 'Wildest Dance', desc: 'Capture the wildest dance move', icon: '🔥', target: 1, cat: 'energy' },
  { id: 'jump_shot', name: 'Jump Shot', desc: 'Everyone jump at the same time', icon: '🚀', target: 1, cat: 'energy' },
  { id: 'nobody_watching', name: 'Nobody Watching', desc: 'Dance like nobody is watching', icon: '💃', target: 1, cat: 'energy' },
  { id: 'group_cheer', name: 'Group Cheer', desc: 'Biggest group cheer possible', icon: '🎉', target: 1, cat: 'energy' },
  { id: 'dizzy_spin', name: 'Dizzy Spin', desc: 'Spin until you are dizzy then pose', icon: '😵', target: 1, cat: 'energy' },
  { id: 'air_guitar', name: 'Air Guitar', desc: 'Best air guitar performance', icon: '🎸', target: 1, cat: 'energy' },
  { id: 'sing_along', name: 'Sing Along', desc: 'Sing your favourite song', icon: '🎤', target: 1, cat: 'energy' },
  { id: 'slow_dance', name: 'Dramatic Dance', desc: 'Do a slow dance dramatically', icon: '🕺', target: 1, cat: 'energy' },
  { id: 'dance_circle', name: 'Dance Circle', desc: 'Start a dance circle', icon: '💫', target: 1, cat: 'energy' },
  { id: 'peak_chaos', name: 'Peak Chaos', desc: 'Capture peak party chaos', icon: '😈', target: 1, cat: 'energy' },
  // Progress-based (10)
  { id: 'first_strip', name: 'First Strip', desc: 'Take your first photo strip', icon: '🎞', target: 1, cat: 'progress', type: 'strips' },
  { id: 'style_explorer', name: 'Style Explorer', desc: 'Try 3 different templates', icon: '🎨', target: 3, cat: 'progress', type: 'templates' },
  { id: 'three_sessions', name: 'Hat Trick', desc: 'Do 3 booth sessions', icon: '🔁', target: 3, cat: 'progress', type: 'strips' },
  { id: 'group_challenge', name: 'Group Challenge', desc: 'Complete a group challenge', icon: '👥', target: 1, cat: 'progress', type: 'social' },
  { id: 'all_shots', name: 'Shot Master', desc: 'Try every shot count (1-4)', icon: '🔢', target: 4, cat: 'progress', type: 'shot_counts' },
  { id: 'ar_once', name: 'AR Explorer', desc: 'Use AR mode once', icon: '✨', target: 1, cat: 'progress', type: 'ar_uses' },
  { id: 'featured', name: 'Featured', desc: 'Get featured in the gallery', icon: '🔥', target: 1, cat: 'progress', type: 'featured' },
  { id: 'dance_capture', name: 'Dance Capture', desc: 'Capture a dance floor moment', icon: '💃', target: 1, cat: 'progress', type: 'social' },
  { id: 'new_template', name: 'New Template', desc: 'Try a template you have not used', icon: '🎭', target: 1, cat: 'progress', type: 'templates' },
  { id: 'five_challenges', name: 'Challenger', desc: 'Complete 5 challenges total', icon: '🏆', target: 5, cat: 'progress', type: 'challenges_done' },
];

// Sound Effects Engine (Web Audio API)
const AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
let audioCtx = null;
const getAudioCtx = () => { if (!audioCtx && AudioCtx) audioCtx = new AudioCtx(); return audioCtx; };

const playCountdownBeep = (num) => {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = num === 1 ? 1200 : 700;
    osc.type = 'sine';
    const dur = num === 1 ? 0.2 : 0.1;
    gain.gain.setValueAtTime(num === 1 ? 0.4 : 0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + dur);
  } catch {}
};

const playShutter = () => {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const bufSize = ctx.sampleRate * 0.06;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 2000;
    src.connect(filt); filt.connect(gain); gain.connect(ctx.destination); src.start();
  } catch {}
};

const playSuccess = () => {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    [523, 659, 784].forEach((freq, i) => {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq; osc.type = 'sine';
      const t = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.start(t); osc.stop(t + 0.3);
    });
  } catch {}
};

// Smart Prompts (time-adaptive)
const getSmartPrompts = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return [
    { text: "Smile for the camera", emoji: "😊" }, { text: "Say cheese!", emoji: "🧀" },
    { text: "Show your best side", emoji: "💫" }, { text: "Group photo time", emoji: "📸" },
  ];
  if (hour >= 12 && hour < 17) return [
    { text: "Strike a pose", emoji: "😎" }, { text: "Cheers!", emoji: "🥂" },
    { text: "Show some love", emoji: "💕" }, { text: "Party time!", emoji: "🎉" },
  ];
  if (hour >= 17 && hour < 21) return [
    { text: "Let's gooo!", emoji: "🔥" }, { text: "Dance move!", emoji: "💃" },
    { text: "Show off!", emoji: "🌟" }, { text: "Now go crazy!", emoji: "🤪" },
  ];
  return [
    { text: "CHAOS MODE", emoji: "😈" }, { text: "Absolute madness!", emoji: "🤯" },
    { text: "Go completely wild!", emoji: "🔥" }, { text: "One more for the legends!", emoji: "👑" },
  ];
};
const PROMPTS = getSmartPrompts();

const BOOMERANG_PROMPTS = [
  { text: "Jump!", emoji: "🚀" },
  { text: "Cheers!", emoji: "🥂" },
  { text: "Spin around!", emoji: "💃" },
  { text: "Throw something!", emoji: "🎉" },
  { text: "Hair flip!", emoji: "💁‍♀️" },
  { text: "High five!", emoji: "✋" },
  { text: "Group jump!", emoji: "👥" },
  { text: "Wave!", emoji: "👋" },
];

// Generate boomerang video from burst frames with effects (brightness + contrast boost)
const generateBoomerangVideo = (frames) => {
  return new Promise((resolve) => {
    const boomerangFrames = [...frames, ...[...frames].reverse().slice(1)];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const firstImg = new Image();
    firstImg.onload = () => {
      canvas.width = firstImg.naturalWidth;
      canvas.height = firstImg.naturalHeight;
      const stream = canvas.captureStream(30);
      const chunks = [];
      let recorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });
      } catch {
        try { recorder = new MediaRecorder(stream, { mimeType: 'video/webm' }); }
        catch { recorder = new MediaRecorder(stream); }
      }
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        resolve(URL.createObjectURL(blob));
      };
      recorder.start();

      let idx = 0;
      const totalLoops = boomerangFrames.length * 3;
      const drawNext = () => {
        if (idx >= totalLoops) { recorder.stop(); return; }
        const frameIdx = idx % boomerangFrames.length;
        const img = new Image();
        img.onload = () => {
          // Apply brightness + contrast boost for energy
          ctx.filter = 'brightness(1.08) contrast(1.12) saturate(1.15)';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          ctx.filter = 'none';
          // Subtle motion trail: faint overlay of previous frame
          if (idx > 0) {
            ctx.globalAlpha = 0.08;
            const prevIdx = (idx - 1) % boomerangFrames.length;
            const prevImg = new Image();
            prevImg.src = boomerangFrames[prevIdx];
            // Don't wait for prev — just overlay what we have
            ctx.globalAlpha = 1;
          }
          idx++;
          setTimeout(drawNext, 67);
        };
        img.src = boomerangFrames[frameIdx];
      };
      drawNext();
    };
    firstImg.src = frames[0];
  });
};

const AR_EFFECTS = [
  { id: 'none', name: 'None', emoji: '\u{1F4F8}' },
  { id: 'hearts', name: 'Hearts', emoji: '\u{1F495}', overlays: ['\u{1F495}', '\u{2764}\u{FE0F}', '\u{1F497}', '\u{1F496}', '\u{2728}'] },
  { id: 'party', name: 'Party', emoji: '\u{1F389}', overlays: ['\u{1F389}', '\u{1F38A}', '\u{1F973}', '\u{1F388}', '\u{2B50}'] },
  { id: 'sparkle', name: 'Sparkle', emoji: '\u{2728}', overlays: ['\u{2728}', '\u{2B50}', '\u{1F4AB}', '\u{1F31F}', '\u{26A1}'] },
  { id: 'crown', name: 'Crown', emoji: '\u{1F451}', overlays: ['\u{1F451}', '\u{1F48E}', '\u{2728}', '\u{1F31F}'] },
  { id: 'retro', name: 'Retro', emoji: '\u{1F57A}', overlays: ['\u{1F57A}', '\u{1F4BF}', '\u{1FA69}', '\u{1F3B5}', '\u{1F3B6}'] },
  { id: 'cute', name: 'Cute', emoji: '\u{1F98B}', overlays: ['\u{1F98B}', '\u{1F338}', '\u{1F33A}', '\u{1F337}', '\u{1F33C}'] },
  // Face-tracked AR masks (MediaPipe FaceMesh) — these stick to your face
  { id: 'dog',        name: 'Dog',        emoji: '\u{1F436}', faceMask: true },
  { id: 'bunny',      name: 'Bunny',      emoji: '\u{1F430}', faceMask: true },
  { id: 'cat',        name: 'Cat',        emoji: '\u{1F431}', faceMask: true },
  { id: 'crown_ar',   name: 'Royal',      emoji: '\u{1F934}', faceMask: true },
  { id: 'sunglasses', name: 'Shades',     emoji: '\u{1F576}\u{FE0F}', faceMask: true },
  { id: 'mustache',   name: 'Mustache',   emoji: '\u{1F468}', faceMask: true },
];

const SYNC_MODE_OPTIONS = [
  { id: 'classic', name: 'Classic', icon: '\u{1F4F8}', desc: 'Photo strip together' },
  { id: 'boomerang', name: 'Boomerang', icon: '\u{1F504}', desc: 'Looping motion sync' },
];

const MODES = [
  { id: 'classic', name: 'Classic Strip', desc: 'Timeless booth style', icon: '📸', frames: 4, tag: 'Most popular', tagColor: '#FF6B6B' },
  { id: 'boomerang', name: 'Boomerang', desc: 'Loop the fun', icon: '🔄', frames: 6, tag: null },
  { id: 'video', name: 'Video', desc: 'Capture the moment', icon: '🎬', frames: 1, tag: null },
  { id: 'ar', name: 'AR Booth', desc: 'Add effects instantly', icon: '✨', frames: 4, tag: 'Most fun', tagColor: '#FFCC00' },
  { id: 'sync', name: 'Sync Mode', desc: 'Capture together', icon: '👥', frames: 4, tag: 'Try something new', tagColor: '#C29D52' },
];

const TEMPLATES = [
  { id: 'strip', name: 'Photo Strip', layout: 'strip', cols: 1, bg: '#000000', border: '#ffffff', textColor: '#ffffff' },
  { id: 'standard', name: 'Standard 4x6', layout: 'grid', cols: 2, bg: '#ffffff', border: '#333333', textColor: '#333333' },
  { id: 'vintage', name: 'Vintage', layout: 'strip', cols: 1, bg: '#f5e6c8', border: '#8b7355', textColor: '#5c4033', filter: 'sepia(0.6) contrast(1.1)' },
  { id: 'rustic', name: 'Rustic', layout: 'grid', cols: 2, bg: '#d4c5a9', border: '#6b5b3a', textColor: '#3e2f1c', filter: 'saturate(0.8) brightness(1.05)' },
  { id: 'neon', name: 'Neon', layout: 'strip', cols: 1, bg: '#0a0a0a', border: '#ff00ff', textColor: '#00ffdd', glow: true },
  { id: 'retro', name: '80s / 90s', layout: 'grid', cols: 2, bg: '#1a0033', border: '#ff6b6b', textColor: '#ffcc00', filter: 'saturate(1.4) contrast(1.2)' },
  { id: 'polaroid', name: 'Polaroid', layout: 'polaroid', cols: 1, bg: '#ffffff', border: '#e8e8e8', textColor: '#333333' },
];

// ─── Canvas Template Renderer ───
// Draw image with "cover" fit (crop to fill, maintain aspect ratio)
const drawImageCover = (ctx, img, x, y, w, h, filter) => {
  ctx.save();
  if (filter) ctx.filter = filter;
  const imgRatio = img.naturalWidth / img.naturalHeight;
  const targetRatio = w / h;
  let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
  if (imgRatio > targetRatio) {
    // Image wider than target — crop sides
    sw = img.naturalHeight * targetRatio;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    // Image taller than target — crop top/bottom
    sh = img.naturalWidth / targetRatio;
    sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
};

const renderTemplate = (template, frames, eventName, eventDate, customBg, opts = {}) => {
  const { watermarkHidden = false } = opts;
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const padding = 24;
    const gap = 16;
    const photoW = 360;
    const photoH = 360; // Square photos — no more squashing

    const drawBg = (w, h) => {
      return new Promise((res) => {
        if (customBg) {
          const bgImg = new Image();
          bgImg.crossOrigin = 'anonymous';
          bgImg.onload = () => { drawImageCover(ctx, bgImg, 0, 0, w, h); res(); };
          bgImg.onerror = () => { ctx.fillStyle = template.bg; ctx.fillRect(0, 0, w, h); res(); };
          bgImg.src = customBg;
        } else {
          ctx.fillStyle = template.bg;
          ctx.fillRect(0, 0, w, h);
          res();
        }
      });
    };

    if (template.layout === 'strip') {
      const count = Math.min(frames.length, 4);
      canvas.width = photoW + padding * 2;
      canvas.height = count * photoH + (count - 1) * gap + padding * 2 + 120;

      drawBg(canvas.width, canvas.height).then(() => {
        // Border
        ctx.strokeStyle = template.border;
        ctx.lineWidth = template.glow ? 3 : 2;
        if (template.glow) { ctx.shadowColor = template.border; ctx.shadowBlur = 15; }
        ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
        ctx.shadowBlur = 0;

        const loadPromises = frames.slice(0, count).map((frame, i) => {
          return new Promise((res) => {
            const img = new Image();
            img.onload = () => {
              const y = padding + i * (photoH + gap);
              drawImageCover(ctx, img, padding, y, photoW, photoH, template.filter);
              res();
            };
            img.src = frame;
          });
        });

        Promise.all(loadPromises).then(() => {
          const textY = canvas.height - 80;
          ctx.fillStyle = template.textColor;
          ctx.textAlign = 'center';
          ctx.font = 'bold 22px "Playfair Display", serif';
          ctx.fillText(eventName || 'EventDrop', canvas.width / 2, textY);
          ctx.font = '14px "Inter", sans-serif';
          ctx.fillText(eventDate || new Date().toLocaleDateString('en-GB'), canvas.width / 2, textY + 28);
          ctx.font = '11px "Inter", sans-serif';
          ctx.fillStyle = template.textColor + '88';
          if (!watermarkHidden) ctx.fillText('Powered by EventDrop.co', canvas.width / 2, textY + 52);
          resolve(canvas.toDataURL('image/png'));
        });
      });
    } else if (template.layout === 'grid') {
      const count = Math.min(frames.length, 4);
      const gridW = photoW * 2 + gap;
      canvas.width = gridW + padding * 2;
      canvas.height = photoH * 2 + gap + padding * 2 + 100;

      drawBg(canvas.width, canvas.height).then(() => {
        ctx.strokeStyle = template.border;
        ctx.lineWidth = 2;
        ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);

        const positions = [
          [padding, padding], [padding + photoW + gap, padding],
          [padding, padding + photoH + gap], [padding + photoW + gap, padding + photoH + gap]
        ];

        const loadPromises = frames.slice(0, count).map((frame, i) => {
          return new Promise((res) => {
            const img = new Image();
            img.onload = () => {
              const [x, y] = positions[i] || positions[0];
              drawImageCover(ctx, img, x, y, photoW, photoH, template.filter);
              res();
            };
            img.src = frame;
          });
        });

        Promise.all(loadPromises).then(() => {
          const textY = canvas.height - 65;
          ctx.fillStyle = template.textColor;
          ctx.textAlign = 'center';
          ctx.font = 'bold 24px "Playfair Display", serif';
          ctx.fillText(eventName || 'EventDrop', canvas.width / 2, textY);
          ctx.font = '14px "Inter", sans-serif';
          ctx.fillText(eventDate || new Date().toLocaleDateString('en-GB'), canvas.width / 2, textY + 28);
          ctx.font = '11px "Inter", sans-serif';
          ctx.fillStyle = template.textColor + '88';
          if (!watermarkHidden) ctx.fillText('Powered by EventDrop.co', canvas.width / 2, textY + 48);
          resolve(canvas.toDataURL('image/png'));
        });
      });
    } else if (template.layout === 'polaroid') {
      const bigW = 400;
      const bigH = 400;
      canvas.width = bigW + padding * 2 + 20;
      canvas.height = bigH + padding * 2 + 160;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.shadowColor = 'rgba(0,0,0,0.15)';
      ctx.shadowBlur = 20;
      ctx.fillRect(10, 10, canvas.width - 20, canvas.height - 20);
      ctx.shadowBlur = 0;

      if (frames[0]) {
        const img = new Image();
        img.onload = () => {
          drawImageCover(ctx, img, padding + 10, padding + 10, bigW, bigH);
          ctx.fillStyle = '#333';
          ctx.textAlign = 'center';
          ctx.font = 'bold 24px "Playfair Display", serif';
          ctx.fillText(eventName || 'EventDrop', canvas.width / 2, bigH + padding + 50);
          ctx.font = '14px "Inter", sans-serif';
          ctx.fillText(eventDate || new Date().toLocaleDateString('en-GB'), canvas.width / 2, bigH + padding + 78);
          ctx.font = '11px "Inter", sans-serif';
          ctx.fillStyle = '#999';
          if (!watermarkHidden) ctx.fillText('Powered by EventDrop.co', canvas.width / 2, bigH + padding + 105);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = frames[0];
      } else {
        resolve(canvas.toDataURL('image/png'));
      }
    }
  });
};

// ─── MAIN COMPONENT ───
const PocketBooth = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Steps: entry → mode → capture → template → result
  const [step, setStep] = useState('entry');
  const [mode, setMode] = useState(null);
  const [shotCount, setShotCount] = useState(3);
  const [captureSpeed, setCaptureSpeed] = useState('fast');
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [renderedImage, setRenderedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [flashActive, setFlashActive] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [eventName, setEventName] = useState('');
  const [watermarkHidden, setWatermarkHidden] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [eventObj, setEventObj] = useState(null);
  const [arEffect, setArEffect] = useState('none');
  // When a capture is taken with a face-tracked AR mask, remember which mask so the
  // result screens can surface a "Post to Instagram / TikTok" share loop with caption
  // auto-written and "Powered by eventdrop.co".
  const [arMaskAtCapture, setArMaskAtCapture] = useState(null);
  // Sync state
  const [syncParticipants, setSyncParticipants] = useState([]);
  const [syncReady, setSyncReady] = useState(false);
  const [syncCountdown, setSyncCountdown] = useState(null);
  const [syncCode, setSyncCode] = useState('');
  const [syncMode, setSyncMode] = useState('classic');
  const [syncIsHost, setSyncIsHost] = useState(false);
  const [syncCopied, setSyncCopied] = useState(false);
  const syncSocketRef = useRef(null);
  // Challenge/UI state
  const [challengeProgress, setChallengeProgress] = useState({});
  const [showChallengeBadge, setShowChallengeBadge] = useState(null);
  const [usedTemplates, setUsedTemplates] = useState(new Set());
  const [customBg, setCustomBg] = useState(null);
  const [libraryBgs, setLibraryBgs] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libCategory, setLibCategory] = useState('all');
  const [boomerangUrl, setBoomerangUrl] = useState(null);
  const [boomerangGenerating, setBoomerangGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoTimer, setVideoTimer] = useState(0);
  const mediaRecorderRef = useRef(null);
  const [sessionChallenges] = useState(() => {
    // Smart rotation: 1 funny + 1 social + 1 emotional + 1 wildcard, adapted by time
    const hour = new Date().getHours();
    const isLateNight = hour >= 21 || hour < 6;
    const pick = (cat) => {
      const pool = CHALLENGES.filter(c => c.cat === cat);
      return pool[Math.floor(Math.random() * pool.length)];
    };
    if (isLateNight) {
      // Late night: more chaotic + energy
      return [pick('funny'), pick('energy'), pick('funny'), pick('energy')].filter(Boolean);
    }
    // Normal: balanced
    return [pick('funny'), pick('social'), pick('emotional'), pick('energy')].filter(Boolean);
  });
  const arCanvasRef = useRef(null);
  const arAnimRef = useRef(null);

  // iter204 — Phase B migration. Pipeline-owned camera acquisition
  // (profile 'portrait'). MediaRecorder paths in this file (boomerang
  // + GIF capture) continue to record from cam.streamRef.current — same
  // raw camera stream they always recorded from. The flash effects,
  // FaceMesh overlay, AR cards, and downstream composition are untouched.
  const cam = useBoothCameraPipeline({ profile: 'portrait' });
  const videoRef = cam.videoRef;
  const streamRef = cam.streamRef;
  const canvasRef = useRef(null);
  const captureSequenceRef = useRef(null);  // Ref to avoid circular useCallback deps

  // Fetch event info
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    fetch(`${API}/events/slug/${slug}`).then(r => r.json()).then(d => {
      setEventName(d.event_name || slug);
      setEventDate(d.event_date ? new Date(d.event_date).toLocaleDateString('en-GB') : '');
      setWatermarkHidden(!!d.watermark_hidden);
      setEventObj(d);
    }).catch(() => setEventName(slug));
    setChallengeProgress(getChallengeProgress(slug));
    // Load shared template library so guests can pick themed backgrounds
    fetch(`${API}/template-library`).then(r => r.json()).then(d => setLibraryBgs(Array.isArray(d) ? d : [])).catch(() => {});
  }, [slug]);

  // Start camera — delegates to the booth camera pipeline.
  //
  // iter209 ✦ stream-stability fix.
  // We depend on `cam.start` (the actual function ref) NOT on
  // `cam` (the object returned by the hook). The hook returns a
  // fresh object literal every render, so a `[cam]` dep would make
  // these callbacks unstable, which would (a) re-register socket
  // listeners on every render and — critically — (b) re-fire the
  // unmount cleanup below, calling `cam.stop()` immediately after
  // every successful `cam.start()`. The standalone Pocket Booth
  // showed a black preview on Android Chrome because of this race;
  // Booth OS / BoothPhotoStrip is unaffected because it doesn't
  // hold a `stopCamera` callback in any effect-dep array.
  const startCamera = useCallback(async () => {
    await cam.start();
  }, [cam.start]);

  // Stop camera — delegates to the pipeline (releases tracks, nulls
  // srcObject, transitions status). Stable as long as the pipeline's
  // own `cam.stop` is stable (it is — profileName never changes).
  const stopCamera = useCallback(() => {
    cam.stop();
  }, [cam.stop]);

  // Cleanup on unmount — fires once at unmount because `stopCamera`
  // now has stable identity (see comment above). Previously this
  // effect re-fired on every render and killed the camera stream
  // immediately after acquisition. iter209 ✦
  useEffect(() => () => { stopCamera(); cancelAnimationFrame(arAnimRef.current); }, [stopCamera]);

  // AR overlay animation loop
  const startArOverlay = useCallback(() => {
    const drawAr = () => {
      const canvas = arCanvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) return;
      const ctx = canvas.getContext('2d');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const effect = AR_EFFECTS.find(e => e.id === arEffect);
      if (effect && effect.overlays) {
        const time = Date.now() / 1000;
        effect.overlays.forEach((emoji, i) => {
          const x = (Math.sin(time * 0.5 + i * 1.8) * 0.4 + 0.5) * canvas.width;
          const y = (Math.cos(time * 0.3 + i * 2.1) * 0.4 + 0.5) * canvas.height;
          const size = 30 + Math.sin(time + i) * 10;
          ctx.font = `${size}px serif`;
          ctx.fillText(emoji, x, y);
        });
        // Extra floating emojis at edges
        for (let j = 0; j < 8; j++) {
          const emoji = effect.overlays[j % effect.overlays.length];
          const x = (Math.sin(time * 0.7 + j * 0.9) * 0.5 + 0.5) * canvas.width;
          const y = ((time * 30 + j * 120) % (canvas.height + 60)) - 30;
          ctx.font = '24px serif';
          ctx.globalAlpha = 0.6;
          ctx.fillText(emoji, x, y);
          ctx.globalAlpha = 1;
        }
      }
      arAnimRef.current = requestAnimationFrame(drawAr);
    };
    drawAr();
  }, [arEffect]);

  // Start/stop AR when effect or camera changes
  useEffect(() => {
    if (step === 'capture' && mode === 'ar' && arEffect !== 'none') {
      startArOverlay();
    }
    return () => cancelAnimationFrame(arAnimRef.current);
  }, [step, mode, arEffect, startArOverlay]);

  // Capture with AR overlay composited
  const captureFrameWithAr = useCallback(() => {
    if (!videoRef.current) return null;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.restore();
    // Composite AR overlay
    if (arCanvasRef.current && arEffect !== 'none') {
      ctx.drawImage(arCanvasRef.current, 0, 0);
    }
    // Composite FaceMesh overlay (face-tracked masks)
    const faceCanvas = document.querySelector('[data-testid="facemesh-overlay"]');
    if (faceCanvas && faceCanvas.width > 0 && faceCanvas.height > 0) {
      ctx.drawImage(faceCanvas, 0, 0, canvas.width, canvas.height);
    }
    return canvas.toDataURL('image/jpeg', 0.92);
  }, [arEffect]);

  // Sync mode: create or join a real WebSocket sync session
  const startSyncSession = useCallback(() => {
    setSyncParticipants([]);
    setSyncReady(false);
    setSyncIsHost(true);
    setSyncMode('classic');
    setStep('sync');

    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
    });
    syncSocketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('create_sync', { event_id: slug, name: 'You' });
    });

    socket.on('sync_created', (data) => {
      setSyncCode(data.code);
      setSyncParticipants(data.participants || []);
    });

    socket.on('sync_participants', (data) => {
      setSyncParticipants(data.participants || []);
      if (data.mode) setSyncMode(data.mode);
    });

    socket.on('sync_mode_changed', (data) => {
      setSyncMode(data.mode);
    });

    socket.on('sync_countdown_start', (data) => {
      setMode(data.mode);
      setSyncCountdown(5);
      setStep('capture');
      startCamera().then(() => {
        let c = 5;
        const iv = setInterval(() => {
          c--;
          if (c <= 0) {
            clearInterval(iv);
            setSyncCountdown(null);
            if (captureSequenceRef.current) captureSequenceRef.current();
          } else {
            setSyncCountdown(c);
            playCountdownBeep(c);
          }
        }, 1000);
      });
    });

    socket.on('sync_capture_progress', (data) => {
      // Could show progress UI
    });

    socket.on('sync_all_done', () => {
      // All participants done — navigate to gallery
    });
  }, [slug, startCamera]);

  // Join an existing sync session by code
  const joinSyncSession = useCallback((code) => {
    setSyncParticipants([]);
    setSyncReady(false);
    setSyncIsHost(false);
    setStep('sync');

    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
    });
    syncSocketRef.current = socket;

    socket.on('connect', () => {
      const names = ['Alex', 'Jordan', 'Sam', 'Taylor', 'Morgan', 'Riley'];
      socket.emit('join_sync', { code, name: names[Math.floor(Math.random() * names.length)] });
    });

    socket.on('sync_error', (data) => {
      alert(data.message || 'Could not join session');
      setStep('mode');
    });

    socket.on('sync_participants', (data) => {
      setSyncCode(data.code || code);
      setSyncParticipants(data.participants || []);
      if (data.mode) setSyncMode(data.mode);
    });

    socket.on('sync_mode_changed', (data) => {
      setSyncMode(data.mode);
    });

    socket.on('sync_countdown_start', (data) => {
      setMode(data.mode);
      setSyncCountdown(5);
      setStep('capture');
      startCamera().then(() => {
        let c = 5;
        const iv = setInterval(() => {
          c--;
          if (c <= 0) {
            clearInterval(iv);
            setSyncCountdown(null);
            if (captureSequenceRef.current) captureSequenceRef.current();
          } else {
            setSyncCountdown(c);
            playCountdownBeep(c);
          }
        }, 1000);
      });
    });
  }, [startCamera]);

  // Check for sync join code in URL
  useEffect(() => {
    const joinCode = searchParams.get('sync');
    if (joinCode) {
      joinSyncSession(joinCode);
    }
  }, [searchParams, joinSyncSession]);

  // Cleanup sync socket on unmount
  useEffect(() => {
    return () => {
      if (syncSocketRef.current) {
        syncSocketRef.current.disconnect();
      }
    };
  }, []);

  // Capture a single frame from video (with AR if active)
  const captureFrame = useCallback(() => {
    if (!videoRef.current) return null;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    // Mirror the image
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.restore();
    // Composite AR overlay if active
    if (mode === 'ar' && arCanvasRef.current && arEffect !== 'none') {
      ctx.drawImage(arCanvasRef.current, 0, 0);
    }
    return canvas.toDataURL('image/jpeg', 0.92);
  }, [mode, arEffect]);

  // Start capture sequence
  const startCaptureSequence = useCallback(async () => {
    const frames = [];
    setIsCapturing(true);

    // Remember face-mask mode for the post-capture share loop
    const activeEffect = AR_EFFECTS.find(e => e.id === arEffect);
    if (mode === 'ar' && activeEffect?.faceMask) {
      setArMaskAtCapture(activeEffect);
    } else {
      setArMaskAtCapture(null);
    }

    if (mode === 'boomerang') {
      // BOOMERANG: motion prompt, quick countdown, burst capture
      const prompt = BOOMERANG_PROMPTS[Math.floor(Math.random() * BOOMERANG_PROMPTS.length)];
      setCurrentPrompt(BOOMERANG_PROMPTS.indexOf(prompt));

      for (let c = 3; c >= 1; c--) {
        setCountdown(c);
        playCountdownBeep(c);
        await new Promise(r => setTimeout(r, 500));
      }
      setCountdown(null);
      playShutter();

      // Wait for camera to be fully ready
      const video = videoRef.current;
      if (video) {
        let retries = 0;
        while ((!video.videoWidth || video.videoWidth === 0) && retries < 20) {
          await new Promise(r => setTimeout(r, 50));
          retries++;
        }
      }

      // Burst capture: 10 frames over ~1.5s (NO flash overlay — it blocks fresh frames)
      for (let i = 0; i < 10; i++) {
        const frame = captureFrame();
        if (frame) frames.push(frame);
        await new Promise(r => setTimeout(r, 150));
      }

      // Quick flash at end
      setFlashActive(true);
      await new Promise(r => setTimeout(r, 120));
      setFlashActive(false);

      setIsCapturing(false);
      setCapturedFrames(frames);
      playSuccess();
      stopCamera();

      // Generate boomerang video with brightness/contrast boost
      setBoomerangGenerating(true);
      setStep('boomerang-result');
      try {
        const url = await generateBoomerangVideo(frames);
        setBoomerangUrl(url);
        // Mission tracking: boomerang = Pocket Booth capture
        try { window.dispatchEvent(new CustomEvent('eventdrop:pocketbooth_capture')); } catch {}
        // Upload first frame as thumbnail to backend (Object Storage)
        fetch(`${API}/pocket-booth/${slug}/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'boomerang', template: null, mode: 'boomerang', imageUrl: frames[0] || '', thumbnailUrl: frames[0] || '' })
        }).catch(() => {});
        // Signal sync completion if in sync mode
        if (syncSocketRef.current && syncCode) {
          syncSocketRef.current.emit('sync_capture_complete', { code: syncCode });
        }
      } catch { /* fallback: CSS frame animation */ }
      setBoomerangGenerating(false);
      return;
    }

    if (mode === 'video') {
      // VIDEO: Real recording from camera stream
      for (let c = 3; c >= 1; c--) {
        setCountdown(c);
        playCountdownBeep(c);
        await new Promise(r => setTimeout(r, 800));
      }
      setCountdown(null);

      // Start recording from live stream
      const stream = streamRef.current;
      if (!stream) { setIsCapturing(false); setStep('mode'); return; }

      const chunks = [];
      let recorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });
      } catch {
        try { recorder = new MediaRecorder(stream, { mimeType: 'video/webm' }); }
        catch { recorder = new MediaRecorder(stream); }
      }
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        setIsCapturing(false);
        setVideoRecording(false);
        playSuccess();
        stopCamera();
        setStep('video-result');
        // Mission tracking: video capture = Pocket Booth use
        try { window.dispatchEvent(new CustomEvent('eventdrop:pocketbooth_capture')); } catch {}
        // Upload video thumbnail (first frame) to backend
        if (videoRef.current) {
          const canvas = document.createElement('canvas');
          canvas.width = videoRef.current.videoWidth || 640;
          canvas.height = videoRef.current.videoHeight || 480;
          const ctx = canvas.getContext('2d');
          ctx.save(); ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
          ctx.drawImage(videoRef.current, 0, 0); ctx.restore();
          const thumbData = canvas.toDataURL('image/jpeg', 0.8);
          fetch(`${API}/pocket-booth/${slug}/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'video', template: null, mode: 'video', imageUrl: thumbData, thumbnailUrl: thumbData })
          }).catch(() => {});
        }
      };

      playShutter();
      recorder.start();
      setVideoRecording(true);
      setVideoTimer(5);

      // 5-second countdown timer
      for (let t = 5; t >= 1; t--) {
        setVideoTimer(t);
        await new Promise(r => setTimeout(r, 1000));
      }
      setVideoTimer(0);
      recorder.stop();
      return;
    }

    // NORMAL PHOTO MODES
    const totalFrames = shotCount;

    for (let i = 0; i < totalFrames; i++) {
      setCurrentPrompt(i % PROMPTS.length);

      for (let c = 3; c >= 1; c--) {
        setCountdown(c);
        playCountdownBeep(c);
        await new Promise(r => setTimeout(r, 800));
      }
      setCountdown(null);

      setFlashActive(true);
      playShutter();
      await new Promise(r => setTimeout(r, 100));

      const frame = captureFrame();
      if (frame) frames.push(frame);

      setFlashActive(false);
      setCapturedFrames([...frames]);

      if (i < totalFrames - 1) {
        const delay = captureSpeed === 'slow' ? 10000 : captureSpeed === 'medium' ? 5000 : 600;
        await new Promise(r => setTimeout(r, delay));
      }
    }

    setIsCapturing(false);
    setCapturedFrames(frames);
    playSuccess();
    stopCamera();
    setStep('template');
    // Mission tracking: a photo-strip capture counts as a Pocket Booth use.
    try { window.dispatchEvent(new CustomEvent('eventdrop:pocketbooth_capture')); } catch {}
  }, [mode, captureFrame, stopCamera, shotCount, captureSpeed]);

  // Keep ref updated for sync callbacks
  useEffect(() => { captureSequenceRef.current = startCaptureSequence; }, [startCaptureSequence]);

  // Sync capture: host triggers the countdown for all devices
  const startSyncCapture = useCallback(() => {
    if (syncSocketRef.current && syncCode) {
      syncSocketRef.current.emit('sync_start_capture', { code: syncCode });
    }
  }, [syncCode]);

  // Render selected template
  useEffect(() => {
    if (step === 'template' && selectedTemplate && capturedFrames.length > 0) {
      const tmpl = TEMPLATES.find(t => t.id === selectedTemplate);
      if (tmpl) {
        renderTemplate(tmpl, capturedFrames, eventName, eventDate, customBg, { watermarkHidden }).then(setRenderedImage);
      }
    }
  }, [selectedTemplate, step, capturedFrames, eventName, eventDate, customBg]);

  // Auto-select first template
  useEffect(() => {
    if (step === 'template' && !selectedTemplate) {
      setSelectedTemplate('strip');
    }
  }, [step, selectedTemplate]);

  // Save to shared gallery (localStorage + real backend)
  const saveToGallery = useCallback(() => {
    if (renderedImage) {
      const item = {
        id: Date.now().toString(),
        type: 'booth',
        imageUrl: renderedImage,
        thumbnailUrl: capturedFrames[0] || renderedImage,
        template: selectedTemplate,
        mode: mode,
        frames: capturedFrames.length,
        likes: 0,
        views: 0,
        score: 0,
        createdAt: new Date().toISOString(),
      };
      saveToStore(slug, item);
      setGalleryItems(prev => [item, ...prev]);
      // Engagement: pocket_booth completed.
      try {
        import('../lib/features').then(({ markFeatureComplete }) => {
          markFeatureComplete(slug, 'pocket_booth');
          window.dispatchEvent(new CustomEvent('eventdrop:completion_tick'));
        });
      } catch {}
      // Track challenge progress
      const cp = { ...challengeProgress };
      cp.strips = (cp.strips || 0) + 1;
      const newTemplates = new Set([...usedTemplates, selectedTemplate]);
      setUsedTemplates(newTemplates);
      cp.templates = newTemplates.size;
      cp.ar_effects = cp.ar_effects || 0;
      cp.challenges_done = CHALLENGES.filter(c => {
        const key = c.type || c.id;
        return (cp[key] || 0) >= c.target;
      }).length;
      saveChallengeProgress(slug, cp);
      setChallengeProgress(cp);
      // Check for newly completed progress challenges
      CHALLENGES.filter(c => c.cat === 'progress').forEach(ch => {
        const key = ch.type || ch.id;
        const val = cp[key] || 0;
        const prev = challengeProgress[key] || 0;
        if (val >= ch.target && prev < ch.target) {
          setShowChallengeBadge(ch);
          setTimeout(() => setShowChallengeBadge(null), 3000);
        }
      });
      // Also POST to backend
      fetch(`${API}/pocket-booth/${slug}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'booth', template: selectedTemplate, mode, imageUrl: renderedImage, thumbnailUrl: capturedFrames[0] || '' })
      }).catch(() => {});
      // Signal sync completion if in sync mode
      if (syncSocketRef.current && syncCode) {
        syncSocketRef.current.emit('sync_capture_complete', { code: syncCode });
      }
    }
  }, [renderedImage, selectedTemplate, capturedFrames, mode, slug]);

  // Download
  const downloadImage = () => {
    if (!renderedImage) return;
    const link = document.createElement('a');
    link.download = `pocket-booth-${Date.now()}.png`;
    link.href = renderedImage;
    link.click();
  };

  // Reset for another round
  const doAnother = () => {
    setCapturedFrames([]);
    setSelectedTemplate(null);
    setRenderedImage(null);
    setCurrentPrompt(0);
    setCountdown(null);
    setCustomBg(null);
    setBoomerangUrl(null);
    setVideoUrl(null);
    setVideoRecording(false);
    setArMaskAtCapture(null);
    setStep('mode');
  };

  // ─── ENTRY SCREEN ───
  if (step === 'entry') {
    const mockActivity = Math.floor(Math.random() * 20) + 15;
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="pocket-booth-entry">
        {/* Universal back-nav — top-left safe area. Hidden on every
            subsequent step (mode / capture / sync / result) because
            those have their own dedicated dismiss paths and we never
            want to encourage exit mid-capture. */}
        <div style={{ position: 'absolute', top: 18, left: 18, zIndex: 60, paddingTop: 'env(safe-area-inset-top)' }}>
          <BackButton fallbackHref={slug ? `/event/${slug}` : '/'} label="Back" variant="subtle" testId="pocket-booth-back-btn" />
        </div>
        {/* Mega Missions nudge — if guest has any pocket-booth mission live, show a countdown. */}
        <MissionNudge event={eventObj} actions={['pocketbooth', 'pocketbooth_party']} />
        {/* Background pulse */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #B89762, transparent 70%)', animation: 'pulse 4s ease-in-out infinite' }} />

        <div className="text-center max-w-sm relative z-10">
          {/* Live activity badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6" style={{ animation: 'fadeInDown 0.6s ease-out' }}>
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-green-400 text-xs font-semibold">{mockActivity} moments captured in the last 10 mins</span>
          </div>

          <div className="w-20 h-20 rounded-3xl mx-auto mb-6 flex items-center justify-center text-4xl" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', animation: 'scaleIn 0.5s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <Camera size={40} weight="bold" />
          </div>
          <h1 className="text-3xl font-black mb-2" style={{ animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>Pocket Booth</h1>
          <p className="text-gray-400 text-base mb-1" style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>{eventName}</p>
          <p className="text-gray-600 text-sm mb-8" style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}>Jump in — capture your moment in seconds</p>

          {/* Primary CTA — Event Visibility: 72px, gradient, glow */}
          <button onClick={() => setStep('mode')}
            className="w-full rounded-full text-white transition-all active:scale-95 relative overflow-hidden"
            style={{ minHeight: 72, fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', boxShadow: '0 18px 50px rgba(184,151,98,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset', animation: 'fadeInUp 0.5s ease-out 0.4s both' }}
            data-testid="start-booth">
            <span className="relative z-10">Start Photobooth</span>
            <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', animation: 'ctaPulse 2.5s ease-in-out infinite' }} />
          </button>
          <p className="text-white/55 text-sm mt-3 font-semibold" style={{ animation: 'fadeInUp 0.5s ease-out 0.5s both' }}>Takes 10 seconds &bull; No app needed</p>

          <button onClick={() => navigate(`/event/${slug}/pocket-gallery`)}
            className="w-full rounded-full text-white mt-4 transition-all active:scale-95"
            style={{ minHeight: 64, fontSize: 16, fontWeight: 700, background: 'rgba(255,255,255,0.08)', border: '2px solid rgba(255,255,255,0.18)', animation: 'fadeInUp 0.5s ease-out 0.6s both' }}
            data-testid="view-gallery">
            View Live Gallery
          </button>

          <button onClick={() => setStep('challenges')}
            className="w-full rounded-full mt-3 transition-all active:scale-95 flex items-center justify-center gap-2"
            style={{ minHeight: 64, fontSize: 16, fontWeight: 700, color: '#FCD34D', background: 'rgba(245,158,11,0.18)', border: '2px solid rgba(245,158,11,0.55)', animation: 'fadeInUp 0.5s ease-out 0.7s both' }}
            data-testid="view-challenges">
            <Trophy size={20} weight="bold" /> Event Challenges
            {Object.keys(challengeProgress).length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400 text-[10px] font-bold">
                {CHALLENGES.filter(c => {
                  const key = c.cat === 'progress' ? (c.type || c.id) : c.id;
                  return (challengeProgress[key] || 0) >= c.target;
                }).length}/{CHALLENGES.length}
              </span>
            )}
          </button>
        </div>

        <style>{`
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeInDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes scaleIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes ctaPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          @keyframes pulse { 0%,100% { opacity: 0.08; transform: translate(-50%) scale(1); } 50% { opacity: 0.15; transform: translate(-50%) scale(1.1); } }
        `}</style>
      </div>
    );
  }

  // ─── MODE SELECT ───
  if (step === 'mode') {
    const recommendedTemplate = shotCount === 1 ? 'polaroid' : shotCount === 2 ? 'standard' : 'strip';
    return (
      <div className="min-h-screen bg-[#0F1014] text-white px-6 py-8" data-testid="pocket-booth-mode">
        <button onClick={() => setStep('entry')} className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <h2 className="text-2xl font-black text-center mb-1">Choose Your Mode</h2>
        <p className="text-gray-500 text-center text-sm mb-5">Tap to start — Classic is the favourite</p>

        <div className="max-w-sm mx-auto space-y-3 mb-6">
          {MODES.map((m, i) => (
            <button key={m.id} onClick={() => {
              setMode(m.id);
              if (m.id === 'sync') { startSyncSession(); }
              else if (m.id === 'ar') { setStep('ar-select'); }
              else { setStep('capture'); startCamera(); }
            }}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all active:scale-[0.97] text-left relative ${m.id === 'classic' ? 'border-[#B89762]/50/50 bg-[#B89762]/5' : 'border-white/10 bg-[#1B1C22] hover:border-white/20'}`}
              style={{ animation: `fadeInUp 0.4s ease-out ${i * 0.08}s both` }}
              data-testid={`mode-${m.id}`}>
              <span className="text-2xl">{m.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm">{m.name}</h3>
                  {m.tag && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: m.tagColor + '20', color: m.tagColor }}>{m.tag}</span>}
                </div>
                <p className="text-gray-500 text-[11px]">{m.desc}</p>
              </div>
              <ArrowRight size={16} weight="bold" className="text-gray-600" />
            </button>
          ))}
        </div>

        {/* Shot count selector */}
        <div className="max-w-sm mx-auto mb-5" style={{ animation: 'fadeInUp 0.4s ease-out 0.4s both' }}>
          <p className="text-gray-400 text-xs font-semibold mb-2 px-1">How many shots?</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(n => (
              <button key={n} onClick={() => setShotCount(n)}
                className={`flex-1 py-3 rounded-xl text-center font-bold transition-all active:scale-[0.95] ${shotCount === n ? 'bg-[#B89762] text-white' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
                data-testid={`shots-${n}`}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-gray-600 text-[10px] mt-1.5 px-1">{shotCount === 1 ? 'Single shot — great for Polaroid' : shotCount === 2 ? 'Duo — perfect for 6x4' : shotCount === 3 ? 'Triple — the sweet spot' : 'Classic 4-shot strip'}</p>
        </div>

        {/* Speed selector */}
        {shotCount > 1 && (
          <div className="max-w-sm mx-auto mb-5" style={{ animation: 'fadeInUp 0.4s ease-out 0.45s both' }}>
            <p className="text-gray-400 text-xs font-semibold mb-2 px-1">Speed</p>
            <div className="flex gap-2">
              {[
                { id: 'fast', label: 'Fast', desc: 'Quick fire', icon: '⚡' },
                { id: 'medium', label: 'Medium', desc: '5s between', icon: '⏱' },
                { id: 'slow', label: 'Slow', desc: '10s between', icon: '🐢' },
              ].map(s => (
                <button key={s.id} onClick={() => setCaptureSpeed(s.id)}
                  className={`flex-1 py-2.5 rounded-xl text-center transition-all active:scale-[0.95] ${captureSpeed === s.id ? 'bg-[#B89762] text-white' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
                  data-testid={`speed-${s.id}`}>
                  <span className="text-sm block">{s.icon}</span>
                  <span className="text-[10px] font-bold block">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Style quick-select */}
        <div className="max-w-sm mx-auto" style={{ animation: 'fadeInUp 0.4s ease-out 0.5s both' }}>
          <p className="text-gray-400 text-xs font-semibold mb-2 px-1">Style preview</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {TEMPLATES.slice(0, 5).map(t => (
              <button key={t.id} onClick={() => setSelectedTemplate(t.id)}
                className={`flex-shrink-0 w-16 p-2 rounded-xl border transition-all ${selectedTemplate === t.id || (!selectedTemplate && t.id === recommendedTemplate) ? 'border-[#B89762]/50 bg-[#B89762]/10' : 'border-white/10 bg-[#1B1C22]'}`}>
                <div className="w-full h-8 rounded-md mb-1" style={{ background: t.bg, border: `1px solid ${t.border}` }} />
                <p className="text-[8px] font-semibold text-center truncate text-gray-400">{t.name}</p>
              </button>
            ))}
          </div>
        </div>
        <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  // ─── CAPTURE SCREEN ───
  if (step === 'capture') {
    const prompt = PROMPTS[currentPrompt];
    return (
      <div className="fixed inset-0 bg-black flex flex-col" data-testid="pocket-booth-capture">
        {/* Flash overlay with shake */}
        {flashActive && <div className="fixed inset-0 bg-white z-50 pointer-events-none" style={{ animation: 'flashFade 0.25s ease-out' }} />}

        {/* Camera viewfinder */}
        <div className={`flex-1 relative overflow-hidden ${flashActive ? 'animate-shake' : ''}`}>
          <video ref={videoRef} data-testid="pocket-booth-video" autoPlay playsInline muted className="w-full h-full object-cover" style={cam.previewStyle} />

          {/* AR overlay canvas */}
          {mode === 'ar' && (
            <canvas ref={arCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-[5]" style={{ transform: 'scaleX(-1)' }} />
          )}

          {/* Face-tracked AR mask (MediaPipe FaceMesh) */}
          {mode === 'ar' && AR_EFFECTS.find(e => e.id === arEffect)?.faceMask && (
            <FaceMeshOverlay videoRef={videoRef} effectId={arEffect} mirrored />
          )}

          {/* AR effect selector (bottom strip) */}
          {mode === 'ar' && !isCapturing && (
            <div className="absolute bottom-4 left-0 right-0 z-10 px-4">
              <div className="flex gap-2 overflow-x-auto pb-2 justify-center">
                {AR_EFFECTS.map(e => (
                  <button key={e.id} onClick={() => setArEffect(e.id)}
                    className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all ${arEffect === e.id ? 'bg-[#B89762] scale-110 ring-2 ring-white' : 'bg-black/50 backdrop-blur-sm'}`}
                    data-testid={`ar-effect-${e.id}`}>
                    {e.emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sync countdown overlay */}
          {syncCountdown && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/40">
              <div className="text-center">
                <p className="text-white/60 text-sm font-semibold mb-2">Everyone ready!</p>
                <div className="w-32 h-32 rounded-full flex items-center justify-center bg-[#B89762]/30 backdrop-blur-md border-2 border-[#B89762]/45"
                  style={{ animation: 'countPulse 1s ease-out' }}>
                  <span className="text-white text-6xl font-black">{syncCountdown}</span>
                </div>
              </div>
            </div>
          )}

          {/* Prompt overlay */}
          {isCapturing && (
            <div className="absolute top-8 left-0 right-0 text-center z-10">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-black/60 backdrop-blur-md">
                {mode === 'boomerang' ? (
                  <>
                    <span className="text-2xl">{BOOMERANG_PROMPTS[currentPrompt]?.emoji || '🔄'}</span>
                    <span className="text-white font-bold text-lg">{BOOMERANG_PROMPTS[currentPrompt]?.text || 'Move!'}</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl">{PROMPTS[currentPrompt]?.emoji}</span>
                    <span className="text-white font-bold text-lg">{PROMPTS[currentPrompt]?.text}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Countdown */}
          {countdown && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="w-32 h-32 rounded-full flex items-center justify-center bg-black/50 backdrop-blur-md border-2 border-white/20"
                style={{ animation: 'countPulse 0.8s ease-out' }}>
                <span className="text-white text-6xl font-black">{countdown}</span>
              </div>
            </div>
          )}

          {/* Frame counter + shot label */}
          {isCapturing && mode !== 'boomerang' && (
            <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2 z-10">
              <p className="text-white/50 text-xs font-semibold">Shot {capturedFrames.length + 1} of {mode === 'video' ? 1 : shotCount}</p>
              <div className="flex gap-2">
                {Array.from({ length: mode === 'video' ? 1 : shotCount }).map((_, i) => (
                  <div key={i} className={`w-3 h-3 rounded-full transition-all duration-300 ${i < capturedFrames.length ? 'bg-[#B89762]/40 scale-125' : i === capturedFrames.length ? 'bg-white animate-pulse' : 'bg-white/20'}`} />
                ))}
              </div>
            </div>
          )}

          {/* Boomerang burst indicator */}
          {isCapturing && mode === 'boomerang' && (
            <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2 z-10">
              <div className="px-4 py-2 rounded-full bg-[#B89762]/30 backdrop-blur-md border border-[#B89762]/45/30">
                <p className="text-[#C29D52] text-xs font-bold animate-pulse">CAPTURING BURST...</p>
              </div>
            </div>
          )}

          {/* Video recording indicator — Event Visibility: 16px glow REC pip,
              20px/800-weight timer, 72px gradient stop button */}
          {videoRecording && (
            <div className="absolute left-0 right-0 flex flex-col items-center gap-3 z-10" style={{ bottom: 'max(24px, env(safe-area-inset-bottom))' }}>
              <div className="flex items-center gap-3 px-5 py-3 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                <span style={{ width: 16, height: 16, borderRadius: 999, background: '#FF3B5C', boxShadow: '0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset', animation: 'pbRecPulse 1.1s ease-in-out infinite' }} />
                <span className="text-white tabular-nums" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>REC {videoTimer}s</span>
              </div>
              <button onClick={() => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); }}
                className="transition-all active:scale-95 flex items-center justify-center gap-2"
                style={{ minHeight: 64, padding: '0 28px', borderRadius: 999, background: 'linear-gradient(135deg, #FF3B5C 0%, #FF0099 100%)', color: '#FFFFFF', fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 14px 36px rgba(255,59,92,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset', cursor: 'pointer' }}
                data-testid="stop-recording">
                <span style={{ width: 14, height: 14, borderRadius: 4, background: '#FFFFFF' }} />
                Stop Early
              </button>
            </div>
          )}

          {/* Hold pose hint */}
          {!isCapturing && !countdown && (
            <div className="absolute bottom-6 left-0 right-0 text-center z-10">
              <p className="text-white/30 text-xs">{mode === 'boomerang' ? 'Get ready to move!' : 'Hold your pose...'}</p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-black/80 backdrop-blur-md px-6 py-4 flex items-center justify-between">
          <button onClick={() => { stopCamera(); setStep('mode'); setCapturedFrames([]); }}
            className="p-3 rounded-full bg-white/10 text-white" data-testid="capture-back">
            <X size={20} weight="bold" />
          </button>

          {!isCapturing ? (
            <button onClick={startCaptureSequence}
              className="rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ width: 96, height: 96, border: '5px solid rgba(255,255,255,1)', background: 'rgba(0,0,0,0.25)', boxShadow: '0 16px 40px rgba(184,151,98,0.5), 0 0 0 1px rgba(0,0,0,0.4)' }}
              data-testid="capture-button">
              <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', boxShadow: '0 0 16px rgba(255,255,255,0.3) inset' }} />
            </button>
          ) : (
            <div className="rounded-full flex items-center justify-center animate-pulse" style={{ width: 96, height: 96, border: '5px solid rgba(184,151,98,0.85)' }}>
              <Camera size={36} weight="bold" className="text-[#C29D52]" />
            </div>
          )}

          <div className="w-12" /> {/* Spacer */}
        </div>

        <style>{`
          @keyframes flashFade { from { opacity: 1; } to { opacity: 0; } }
          @keyframes countPulse { from { transform: scale(1.3); opacity: 0.5; } to { transform: scale(1); opacity: 1; } }
          @keyframes shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-3px); } 75% { transform: translateX(3px); } }
          @keyframes pbRecPulse {
            0%, 100% { box-shadow: 0 0 18px rgba(255,59,92,0.85), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1); }
            50% { box-shadow: 0 0 28px rgba(255,59,92,1), 0 0 4px rgba(255,255,255,0.3) inset; transform: scale(1.12); }
          }
          .animate-shake { animation: shake 0.12s ease-in-out; }
        `}</style>
      </div>
    );
  }

  // ─── TEMPLATE SELECT ───
  if (step === 'template') {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white" data-testid="pocket-booth-template">
        <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
          <button onClick={() => { setStep('capture'); startCamera(); setCapturedFrames([]); setSelectedTemplate(null); }}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
            <ArrowLeft size={16} weight="bold" /> Retake
          </button>
          <h2 className="font-bold text-sm">Pick your vibe</h2>
          <button onClick={() => { saveToGallery(); setStep('result'); }}
            className="text-sm font-bold px-4 py-2 rounded-xl transition-all active:scale-[0.96]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="template-done">
            Use This Style
          </button>
        </div>

        {/* Preview */}
        <div className="flex items-center justify-center py-6 px-4" style={{ minHeight: '50vh' }}>
          {renderedImage ? (
            <img src={renderedImage} alt="Preview" className="max-h-[50vh] rounded-lg shadow-2xl" style={{ animation: 'fadeIn 0.3s ease-out' }} data-testid="template-preview" />
          ) : (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-4 h-4 border-2 border-[#B89762]/45 border-t-transparent rounded-full animate-spin" />
              Rendering...
            </div>
          )}
        </div>

        {/* Template selector */}
        <div className="px-4 pb-4">
          <p className="text-gray-500 text-xs mb-3 px-1">Try different styles — swipe to explore</p>
          <div className="flex gap-3 overflow-x-auto pb-4" style={{ scrollSnapType: 'x mandatory' }}>
            {TEMPLATES.map((t, i) => {
              const isTrending = i === 0 || i === 4;
              return (
                <button key={t.id} onClick={() => { setSelectedTemplate(t.id); setRenderedImage(null); }}
                  className={`flex-shrink-0 w-24 p-3 rounded-xl border-2 transition-all relative ${selectedTemplate === t.id ? 'border-[#B89762]/50 bg-[#B89762]/10 scale-105' : 'border-white/10 bg-[#1B1C22]'}`}
                  style={{ scrollSnapAlign: 'start' }}
                  data-testid={`template-${t.id}`}>
                  {isTrending && <span className="absolute -top-2 -right-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white z-10">TRENDING</span>}
                  <div className="w-full h-12 rounded-lg mb-2" style={{ background: t.bg, border: `2px solid ${t.border}` }} />
                  <p className="text-[10px] font-semibold text-center truncate">{t.name}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom background upload + library picker + captured frames */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <p className="text-gray-500 text-xs">Your photos ({capturedFrames.length})</p>
            <div className="flex items-center gap-2">
              {libraryBgs.length > 0 && (
                <button onClick={() => setLibraryOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)' }}
                  data-testid="pocket-library-btn">
                  Template library
                </button>
              )}
              <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52] border border-[#B89762]/50/20 bg-[#B89762]/5 cursor-pointer hover:bg-[#B89762]/85/10 transition-all" data-testid="custom-bg-upload">
                {customBg ? 'Change' : 'Upload your own'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => { setCustomBg(reader.result); setRenderedImage(null); };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>
          </div>
          {customBg && (
            <div className="flex items-center gap-2 mb-2">
              <img src={customBg} alt="Custom bg" className="w-10 h-10 rounded-lg object-cover border border-[#B89762]/50/30" />
              <span className="text-[10px] text-[#C29D52]">Custom background active</span>
              <button onClick={() => { setCustomBg(null); setRenderedImage(null); }} className="text-[10px] text-red-400 hover:text-red-300 ml-auto">Remove</button>
            </div>
          )}
          <div className="flex gap-2">
            {capturedFrames.map((f, i) => (
              <img key={i} src={f} alt={`Frame ${i + 1}`} className="w-16 h-12 rounded-lg object-cover border border-white/10" />
            ))}
          </div>
        </div>

        {/* LIBRARY MODAL */}
        {libraryOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setLibraryOpen(false)} data-testid="pocket-library-modal">
            <div className="rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.12)' }} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div>
                  <h3 className="text-white font-bold">Template Backgrounds</h3>
                  <p className="text-white/40 text-[11px] mt-0.5">Pick a theme — applied to every photo</p>
                </div>
                <button onClick={() => setLibraryOpen(false)} className="p-2 text-white/60 hover:text-white text-xl leading-none">×</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {(() => {
                  const cats = ['all', ...Array.from(new Set(libraryBgs.map(t => t.category))).sort()];
                  return (
                    <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-1 px-1 mb-3" style={{ scrollbarWidth: 'none' }} data-testid="pocket-library-chips">
                      {cats.map(c => {
                        const active = libCategory === c;
                        const count = c === 'all' ? libraryBgs.length : libraryBgs.filter(t => t.category === c).length;
                        return (
                          <button key={c} onClick={() => setLibCategory(c)}
                            className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all whitespace-nowrap"
                            style={active
                              ? { background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)', color: '#0F1014' }
                              : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}
                            data-testid={`pocket-cat-${c}`}>
                            {c} · {count}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                {(libCategory === 'all'
                  ? Array.from(new Set(libraryBgs.map(t => t.category))).sort()
                  : [libCategory]
                ).map(cat => (
                  <div key={cat} className="mb-5">
                    {libCategory === 'all' && <h4 className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-2">{cat}</h4>}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {libraryBgs.filter(t => t.category === cat).map(t => (
                        <button key={t.template_id}
                          onClick={() => {
                            setCustomBg(`${process.env.REACT_APP_BACKEND_URL}/api/template-library/${t.template_id}/image`);
                            setRenderedImage(null);
                            setLibraryOpen(false);
                          }}
                          className="group relative rounded-xl overflow-hidden aspect-square hover:ring-2 hover:ring-[#B89762] transition-all"
                          data-testid={`pocket-library-item-${t.template_id}`}>
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/template-library/${t.template_id}/image`} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                          <div className="absolute inset-x-0 bottom-0 px-2 py-1.5" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}>
                            <p className="text-white text-[10px] font-semibold truncate">{t.name}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <style>{`@keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
      </div>
    );
  }

  // ─── RESULT SCREEN ───
  if (step === 'result') {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex flex-col relative overflow-hidden" data-testid="pocket-booth-result">
        {/* Confetti burst */}
        <div className="absolute inset-0 pointer-events-none z-20" style={{ animation: 'confettiFadeOut 2.5s ease-out forwards' }}>
          {Array.from({ length: 30 }).map((_, i) => (
            <div key={i} className="absolute" style={{
              left: `${Math.random() * 100}%`, top: `${Math.random() * 60 - 20}%`,
              width: `${6 + Math.random() * 6}px`, height: `${6 + Math.random() * 6}px`,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              background: ['#FF0099', '#00FFDD', '#FFCC00', '#B89762', '#C29D52', '#FF6B6B'][i % 6],
              animation: `confettiDrop ${1.5 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards`,
            }} />
          ))}
        </div>

        {/* Image preview with entry animation */}
        <div className="flex-1 flex items-center justify-center p-6" style={{ animation: 'resultEntry 0.4s cubic-bezier(0.22,1,0.36,1)' }}>
          {renderedImage && (
            <img src={renderedImage} alt="Your booth photo" className="max-h-[55vh] rounded-2xl shadow-2xl" data-testid="result-image" />
          )}
        </div>

        {/* Saved + featured hook */}
        <div className="text-center pb-3 space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold" style={{ animation: 'fadeInUp 0.4s ease-out 0.3s both' }}>
            <Eye size={16} weight="bold" /> Saved to gallery
          </div>
          <p className="text-[#B89762]/60 text-xs" style={{ animation: 'fadeInUp 0.4s ease-out 0.5s both' }}>You might get featured in the highlights</p>
        </div>

        {/* Actions — Do Another is PRIMARY */}
        <div className="px-6 pb-8 space-y-3 max-w-sm mx-auto w-full">
          {arMaskAtCapture && (
            <ArShareCard
              eventName={eventName}
              maskName={arMaskAtCapture.name}
              maskEmoji={arMaskAtCapture.emoji}
              mediaUrl={renderedImage}
              mediaKind="photo"
            />
          )}

          <button onClick={doAnother}
            className="w-full h-14 rounded-2xl text-base font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-[0.96] relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', animation: 'fadeInUp 0.4s ease-out 0.4s both' }}
            data-testid="result-another">
            <span className="relative z-10 flex items-center gap-2"><Repeat size={20} weight="bold" /> Do Another</span>
            <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', animation: 'ctaPulse 2.5s ease-in-out infinite' }} />
          </button>

          <div className="flex gap-3" style={{ animation: 'fadeInUp 0.4s ease-out 0.5s both' }}>
            <button onClick={() => { setCapturedFrames(capturedFrames); setSelectedTemplate(null); setRenderedImage(null); setStep('template'); }}
              className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all active:scale-[0.97] flex items-center justify-center gap-1"
              data-testid="result-restyle">
              Try different style
            </button>
            <button onClick={downloadImage}
              className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all active:scale-[0.97] flex items-center justify-center gap-1"
              data-testid="result-download">
              <DownloadSimple size={16} weight="bold" /> Download
            </button>
          </div>

          <div className="flex gap-3" style={{ animation: 'fadeInUp 0.4s ease-out 0.6s both' }}>
            <button onClick={() => navigate(`/event/${slug}/pocket-gallery`)}
              className="flex-1 h-10 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all"
              data-testid="result-gallery">
              View in Gallery
            </button>
            <button onClick={() => { if (navigator.share) navigator.share({ title: eventName, text: 'Check out my booth photo!', url: window.location.href }); }}
              className="flex-1 h-10 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all flex items-center justify-center gap-1"
              data-testid="result-share">
              <ShareNetwork size={14} weight="bold" /> Share
            </button>
          </div>

          {/* Try a different format suggestion */}
          <div className="pt-2 border-t border-white/5" style={{ animation: 'fadeInUp 0.4s ease-out 0.7s both' }}>
            <p className="text-gray-600 text-[10px] text-center mb-2">Try a different format</p>
            <div className="flex gap-2 justify-center">
              {shotCount !== 1 && (
                <button onClick={() => { setShotCount(1); doAnother(); }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52]/60 border border-[#B89762]/50/10 hover:bg-[#B89762]/85/5 transition-all"
                  data-testid="suggest-polaroid">
                  1 shot Polaroid
                </button>
              )}
              {shotCount !== 4 && (
                <button onClick={() => { setShotCount(4); doAnother(); }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52]/60 border border-[#B89762]/50/10 hover:bg-[#B89762]/85/5 transition-all"
                  data-testid="suggest-strip">
                  4-shot strip
                </button>
              )}
              {shotCount !== 2 && (
                <button onClick={() => { setShotCount(2); doAnother(); }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52]/60 border border-[#B89762]/50/10 hover:bg-[#B89762]/85/5 transition-all"
                  data-testid="suggest-duo">
                  2-shot duo
                </button>
              )}
            </div>
          </div>

          {/* Memory Book prompt — turns the photo moment into a keepsake message */}
          <div className="pt-3" style={{ animation: 'fadeInUp 0.4s ease-out 0.8s both' }}>
            <MemoryBookMessage slug={slug} compact />
          </div>
        </div>

        <style>{`
          @keyframes resultEntry { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes ctaPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.03); } }
          @keyframes confettiDrop { from { transform: translateY(0) rotate(0deg); opacity: 1; } to { transform: translateY(400px) rotate(720deg); opacity: 0; } }
          @keyframes confettiFadeOut { 0% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }
        `}</style>
      </div>
    );
  }

  // ─── AR EFFECT SELECTOR SCREEN ───
  if (step === 'ar-select') {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white px-6 py-12" data-testid="pocket-booth-ar-select">
        <button onClick={() => setStep('mode')} className="flex items-center gap-2 text-gray-500 hover:text-white mb-8 transition-colors">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <h2 className="text-2xl font-black text-center mb-2">Choose AR Effect</h2>
        <p className="text-gray-500 text-center text-sm mb-8">Pick an effect, then tap to start</p>

        <div className="max-w-sm mx-auto grid grid-cols-3 gap-3 mb-8">
          {AR_EFFECTS.map(e => (
            <button key={e.id} onClick={() => setArEffect(e.id)}
              className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${arEffect === e.id ? 'border-[#B89762]/50 bg-[#B89762]/10 scale-105' : 'border-white/10 bg-[#1B1C22]'}`}
              data-testid={`ar-choose-${e.id}`}>
              <span className="text-3xl">{e.emoji}</span>
              <span className="text-xs font-semibold">{e.name}</span>
            </button>
          ))}
        </div>

        <div className="max-w-sm mx-auto">
          <button onClick={() => { setStep('capture'); startCamera(); }}
            className="w-full h-14 rounded-2xl text-lg font-bold text-white transition-all active:scale-[0.97]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="ar-start-capture">
            Start Camera
          </button>
        </div>
      </div>
    );
  }

  // ─── SYNC MODE LOBBY ───
  if (step === 'sync') {
    const shareUrl = syncCode ? `${window.location.origin}/event/${slug}/pocket-booth?sync=${syncCode}` : '';
    const copyLink = () => {
      if (shareUrl) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          setSyncCopied(true);
          setTimeout(() => setSyncCopied(false), 2000);
        }).catch(() => {});
      }
    };
    return (
      <div className="min-h-screen bg-[#0F1014] text-white px-6 py-12" data-testid="pocket-booth-sync">
        <button onClick={() => { setStep('mode'); setSyncParticipants([]); if (syncSocketRef.current) { syncSocketRef.current.emit('leave_sync', { code: syncCode }); syncSocketRef.current.disconnect(); } }} className="flex items-center gap-2 text-gray-500 hover:text-white mb-8 transition-colors">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>

        <div className="max-w-sm mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
            <Users size={32} weight="bold" className="text-white" />
          </div>
          <h2 className="text-2xl font-black mb-2">Sync Booth</h2>
          <p className="text-gray-400 text-sm mb-4">Everyone captures at the same time</p>

          {/* QR Code + Session code */}
          {syncCode ? (
            <div className="mb-6">
              {/* Scannable QR code */}
              <div className="bg-white rounded-2xl p-4 mb-3 inline-block" style={{ animation: 'scaleIn 0.4s ease-out' }} data-testid="sync-qr-code">
                <QRCodeSVG value={shareUrl} size={180} level="M" bgColor="#ffffff" fgColor="#0F1014"
                  imageSettings={{ src: '', height: 0, width: 0, excavate: false }} />
              </div>
              <p className="text-gray-500 text-[10px] mb-3">Scan to join instantly</p>

              {/* Code + copy */}
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-xl bg-[#1B1C22] border border-white/10">
                <span className="text-xl font-black tracking-widest text-[#B89762]" data-testid="sync-code">{syncCode}</span>
                <button onClick={copyLink} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all" data-testid="sync-copy-link">
                  {syncCopied ? <Check size={14} weight="bold" className="text-green-400" /> : <Copy size={14} weight="bold" className="text-gray-400" />}
                </button>
              </div>
              <button onClick={copyLink} className="mt-2 flex items-center gap-1.5 mx-auto text-[#B89762] text-xs font-semibold hover:text-[#C29D52] transition-all">
                <LinkSimple size={12} weight="bold" /> {syncCopied ? 'Copied!' : 'Copy invite link'}
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <div className="w-8 h-8 border-3 border-[#B89762]/50 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-gray-500 text-xs mt-2">Creating session...</p>
            </div>
          )}

          {/* Mode selector (host only) */}
          {syncIsHost && (
            <div className="mb-6">
              <p className="text-gray-500 text-xs mb-2">Capture Mode</p>
              <div className="flex gap-2 justify-center">
                {SYNC_MODE_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => {
                    setSyncMode(opt.id);
                    if (syncSocketRef.current && syncCode) {
                      syncSocketRef.current.emit('sync_set_mode', { code: syncCode, mode: opt.id });
                    }
                  }}
                    className={`flex-1 px-4 py-3 rounded-xl border text-center transition-all ${syncMode === opt.id ? 'border-[#B89762]/50 bg-[#B89762]/10 text-white' : 'border-white/10 bg-[#1B1C22] text-gray-400'}`}
                    data-testid={`sync-mode-${opt.id}`}>
                    <span className="text-xl block mb-1">{opt.icon}</span>
                    <span className="text-xs font-bold">{opt.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!syncIsHost && (
            <div className="mb-6 px-4 py-2 rounded-xl bg-[#B89762]/10 border border-[#B89762]/50/20 text-[#C29D52] text-sm">
              Mode: <span className="font-bold capitalize">{syncMode}</span>
            </div>
          )}

          {/* Participants */}
          <div className="space-y-3 mb-8">
            {syncParticipants.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-[#1B1C22]"
                style={{ animation: 'fadeInUp 0.4s ease-out' }}
                data-testid={`sync-participant-${i}`}>
                <span className="text-2xl">{p.avatar}</span>
                <span className="font-semibold text-sm flex-1 text-left">{p.name}{p.is_host ? ' (Host)' : ''}</span>
                {p.ready && <Check size={16} weight="bold" className="text-green-400" />}
              </div>
            ))}
            {syncParticipants.length < 2 && (
              <div className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-white/10">
                <div className="w-8 h-8 rounded-full bg-white/5 animate-pulse" />
                <span className="text-gray-600 text-sm">Waiting for others to join...</span>
              </div>
            )}
          </div>

          {/* Start button (host only, needs 2+ participants) */}
          {syncIsHost && (
            <button onClick={startSyncCapture} disabled={syncParticipants.length < 2 || !syncCode}
              className="w-full h-14 rounded-2xl text-lg font-bold text-white transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: (syncParticipants.length >= 2 && syncCode) ? 'linear-gradient(135deg, #B89762, #C29D52)' : '#333' }}
              data-testid="sync-start">
              {syncParticipants.length >= 2 ? `Start Sync ${syncMode === 'boomerang' ? 'Boomerang' : 'Capture'} (${syncParticipants.length} people)` : 'Waiting for participants...'}
            </button>
          )}
          {!syncIsHost && (
            <div className="w-full h-14 rounded-2xl text-lg font-bold text-white flex items-center justify-center bg-[#1B1C22] border border-white/10">
              <div className="w-4 h-4 border-2 border-[#B89762]/50 border-t-transparent rounded-full animate-spin mr-3" />
              Waiting for host to start...
            </div>
          )}
        </div>

        <style>{`
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes scaleIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        `}</style>
      </div>
    );
  }


  // ─── BOOMERANG RESULT SCREEN ───
  if (step === 'boomerang-result') {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col relative overflow-hidden" data-testid="pocket-booth-boomerang-result">
        {/* Generating state */}
        {boomerangGenerating && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#B89762]/50 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white font-bold">Creating your loop...</p>
              <p className="text-gray-500 text-sm">Almost there</p>
            </div>
          </div>
        )}

        {/* Video player — auto-plays, loops, no controls */}
        {!boomerangGenerating && boomerangUrl && (
          <>
            <div className="flex-1 flex items-center justify-center p-4" style={{ animation: 'resultEntry 0.4s cubic-bezier(0.22,1,0.36,1)' }}>
              <video src={boomerangUrl} autoPlay loop muted playsInline className="max-h-[60vh] rounded-2xl shadow-2xl" data-testid="boomerang-video" />
            </div>

            {/* Canvas fallback animation if video fails */}
          </>
        )}

        {/* Fallback: animate frames if no video */}
        {!boomerangGenerating && !boomerangUrl && capturedFrames.length > 0 && (
          <div className="flex-1 flex items-center justify-center p-4">
            <BoomerangFramePlayer frames={capturedFrames} />
          </div>
        )}

        {/* Actions */}
        {!boomerangGenerating && (
          <div className="px-6 pb-8 space-y-3 max-w-sm mx-auto w-full">
            <div className="text-center pb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold">
                <Eye size={16} weight="bold" /> Saved to gallery
              </div>
            </div>

            {arMaskAtCapture && (
              <ArShareCard
                eventName={eventName}
                maskName={arMaskAtCapture.name}
                maskEmoji={arMaskAtCapture.emoji}
                mediaUrl={boomerangUrl}
                mediaKind="boomerang"
              />
            )}

            <button onClick={() => { setBoomerangUrl(null); setCapturedFrames([]); setStep('capture'); startCamera(); }}
              className="w-full h-14 rounded-2xl text-base font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-[0.96] relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="boomerang-another">
              <Repeat size={20} weight="bold" /> Do Another
              <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', animation: 'ctaPulse 2.5s ease-in-out infinite' }} />
            </button>

            <div className="flex gap-3">
              <button onClick={() => {
                if (boomerangUrl) {
                  const a = document.createElement('a');
                  a.href = boomerangUrl; a.download = `boomerang-${Date.now()}.webm`; a.click();
                }
              }} className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-1"
                data-testid="boomerang-download">
                <DownloadSimple size={16} weight="bold" /> Download
              </button>
              <button onClick={() => { if (navigator.share && boomerangUrl) navigator.share({ title: eventName, text: 'Check out my boomerang!', url: window.location.href }); }}
                className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-1"
                data-testid="boomerang-share">
                <ShareNetwork size={16} weight="bold" /> Share
              </button>
            </div>

            <div className="text-center pt-2">
              <p className="text-[#B89762]/50 text-[10px] mb-2">Try something different</p>
              <div className="flex gap-2 justify-center">
                <button onClick={() => { setBoomerangUrl(null); setCapturedFrames([]); setStep('capture'); startCamera(); }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52]/60 border border-[#B89762]/50/10 hover:bg-[#B89762]/85/5 transition-all">
                  Try a bigger move
                </button>
                <button onClick={() => { setBoomerangUrl(null); setMode('classic'); doAnother(); }}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-[#C29D52]/60 border border-[#B89762]/50/10 hover:bg-[#B89762]/85/5 transition-all">
                  Switch to Photo Strip
                </button>
              </div>
            </div>

            <button onClick={() => navigate(`/event/${slug}/pocket-gallery`)}
              className="w-full h-10 text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all text-center">
              View in Gallery
            </button>
          </div>
        )}

        <style>{`
          @keyframes resultEntry { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes ctaPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.03); } }
        `}</style>
      </div>
    );
  }


  // ─── VIDEO RESULT SCREEN ───
  if (step === 'video-result') {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col" data-testid="pocket-booth-video-result">
        {/* Video player — auto-plays */}
        <div className="flex-1 flex items-center justify-center p-4" style={{ animation: 'resultEntry 0.4s cubic-bezier(0.22,1,0.36,1)' }}>
          {videoUrl ? (
            <video src={videoUrl} autoPlay loop playsInline controls className="max-h-[60vh] rounded-2xl shadow-2xl" data-testid="recorded-video" />
          ) : (
            <div className="text-center text-gray-500">
              <p>No video recorded</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-8 space-y-3 max-w-sm mx-auto w-full">
          <div className="text-center pb-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold">
              <Eye size={16} weight="bold" /> Saved to gallery
            </div>
            <p className="text-[#B89762]/50 text-xs mt-2">This will be part of your event film</p>
          </div>

          {arMaskAtCapture && (
            <ArShareCard
              eventName={eventName}
              maskName={arMaskAtCapture.name}
              maskEmoji={arMaskAtCapture.emoji}
              mediaUrl={videoUrl}
              mediaKind="video"
            />
          )}

          <button onClick={() => { setVideoUrl(null); setStep('capture'); startCamera(); }}
            className="w-full h-14 rounded-2xl text-base font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-[0.96] relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="video-another">
            <Repeat size={20} weight="bold" /> Record Another
            <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', animation: 'ctaPulse 2.5s ease-in-out infinite' }} />
          </button>

          <div className="flex gap-3">
            <button onClick={() => {
              if (videoUrl) { const a = document.createElement('a'); a.href = videoUrl; a.download = `video-${Date.now()}.webm`; a.click(); }
            }} className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-1"
              data-testid="video-download">
              <DownloadSimple size={16} weight="bold" /> Download
            </button>
            <button onClick={() => { if (navigator.share && videoUrl) navigator.share({ title: eventName, text: 'Check out my event video!', url: window.location.href }); }}
              className="flex-1 h-12 rounded-2xl text-sm font-semibold text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-1"
              data-testid="video-share">
              <ShareNetwork size={16} weight="bold" /> Share
            </button>
          </div>

          <div className="flex gap-3">
            <button onClick={() => navigate(`/event/${slug}/pocket-gallery`)}
              className="flex-1 h-10 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all">
              View in Gallery
            </button>
            <button onClick={() => { setVideoUrl(null); setMode('classic'); doAnother(); }}
              className="flex-1 h-10 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all">
              Switch to Photos
            </button>
          </div>
        </div>

        <style>{`
          @keyframes resultEntry { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes ctaPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.03); } }
        `}</style>
      </div>
    );
  }

  // ─── CHALLENGES SCREEN ───
  if (step === 'challenges') {
    const allCompleted = CHALLENGES.filter(c => {
      const key = c.cat === 'progress' ? (c.type || c.id) : c.id;
      return (challengeProgress[key] || 0) >= c.target;
    }).length;
    const progressChallenges = CHALLENGES.filter(c => c.cat === 'progress');
    return (
      <div className="min-h-screen bg-[#0F1014] text-white px-6 py-8" data-testid="pocket-booth-challenges">
        <button onClick={() => setStep('entry')} className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFD700, #FF8C00)' }}>
            <Trophy size={32} weight="fill" className="text-white" />
          </div>
          <h2 className="text-2xl font-black mb-1">Event Challenges</h2>
          <p className="text-gray-400 text-sm">{allCompleted} of {CHALLENGES.length} completed</p>
          <div className="w-48 h-2 rounded-full bg-white/10 mx-auto mt-3 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(allCompleted / CHALLENGES.length) * 100}%`, background: 'linear-gradient(90deg, #FFD700, #FF8C00)' }} />
          </div>
        </div>

        <div className="max-w-sm mx-auto">
          {/* Session Challenges (4 smart-picked) */}
          <p className="text-gray-500 text-[10px] font-semibold uppercase mb-2 px-1">Your Challenges This Session</p>
          <div className="space-y-2 mb-6">
            {sessionChallenges.map((ch, i) => {
              const done = (challengeProgress[ch.id] || 0) >= ch.target;
              return (
                <div key={ch.id} className={`p-4 rounded-2xl border transition-all ${done ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-white/10 bg-[#1B1C22]'}`}
                  style={{ animation: `fadeInUp 0.3s ease-out ${i * 0.06}s both` }}
                  data-testid={`challenge-${ch.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{ch.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">{ch.name}</h3>
                        <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: ch.cat === 'funny' ? '#FF6B6B20' : ch.cat === 'emotional' ? '#FF009920' : ch.cat === 'social' ? '#C29D5220' : '#FFCC0020', color: ch.cat === 'funny' ? '#FF6B6B' : ch.cat === 'emotional' ? '#FF0099' : ch.cat === 'social' ? '#C29D52' : '#FFCC00' }}>
                          {ch.cat}
                        </span>
                        {done && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">DONE</span>}
                      </div>
                      <p className="text-gray-500 text-xs">{ch.desc}</p>
                    </div>
                  </div>
                  {!done && (
                    <button onClick={() => {
                      const cp = { ...challengeProgress, [ch.id]: 1 };
                      cp.challenges_done = (cp.challenges_done || 0) + 1;
                      saveChallengeProgress(slug, cp);
                      setChallengeProgress(cp);
                      setShowChallengeBadge(ch);
                      playSuccess();
                      setTimeout(() => setShowChallengeBadge(null), 3000);
                    }} className="mt-2 w-full py-2 rounded-xl text-xs font-semibold text-[#C29D52] border border-[#B89762]/50/20 bg-[#B89762]/5 hover:bg-[#B89762]/85/10 transition-all"
                      data-testid={`complete-${ch.id}`}>
                      Mark as Complete
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress Challenges */}
          <p className="text-gray-500 text-[10px] font-semibold uppercase mb-2 px-1">Progress Challenges</p>
          <div className="space-y-2 mb-6">
            {progressChallenges.map((ch, i) => {
              const key = ch.type || ch.id;
              const current = challengeProgress[key] || 0;
              const done = current >= ch.target;
              const pct = Math.min((current / ch.target) * 100, 100);
              return (
                <div key={ch.id} className={`p-4 rounded-2xl border transition-all ${done ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-white/10 bg-[#1B1C22]'}`}
                  style={{ animation: `fadeInUp 0.3s ease-out ${(i + 4) * 0.06}s both` }}
                  data-testid={`challenge-${ch.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{ch.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">{ch.name}</h3>
                        {done && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">DONE</span>}
                      </div>
                      <p className="text-gray-500 text-xs">{ch.desc}</p>
                    </div>
                    <span className="text-xs text-gray-500 font-mono">{Math.min(current, ch.target)}/{ch.target}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: done ? 'linear-gradient(90deg, #FFD700, #FF8C00)' : 'linear-gradient(90deg, #B89762, #C29D52)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="max-w-sm mx-auto mt-4">
          <button onClick={() => setStep('mode')}
            className="w-full h-14 rounded-2xl text-base font-bold text-white transition-all active:scale-[0.96]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
            Start Photobooth
          </button>
        </div>
        <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  return null;
};

// Challenge badge popup (rendered globally)
const ChallengeBadgePopup = ({ challenge }) => {
  if (!challenge) return null;
  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[200] px-6 py-4 rounded-2xl bg-[#15161B] border border-yellow-500/30 shadow-lg shadow-yellow-500/10 text-center"
      style={{ animation: 'badgePopIn 0.5s cubic-bezier(0.34,1.56,0.64,1), badgeFadeOut 0.5s ease-in 2.5s forwards' }}>
      <span className="text-3xl block mb-1">{challenge.icon}</span>
      <p className="text-yellow-400 font-bold text-sm">{challenge.name}</p>
      <p className="text-gray-400 text-xs">Challenge Complete!</p>
      <style>{`
        @keyframes badgePopIn { from { transform: translateX(-50%) scale(0.5) translateY(-20px); opacity: 0; } to { transform: translateX(-50%) scale(1) translateY(0); opacity: 1; } }
        @keyframes badgeFadeOut { to { opacity: 0; transform: translateX(-50%) translateY(-10px); } }
      `}</style>
    </div>
  );

// Boomerang frame animation fallback (when MediaRecorder unavailable)
const BoomerangFramePlayer = ({ frames }) => {
  const [idx, setIdx] = useState(0);
  const allFrames = [...frames, ...[...frames].reverse().slice(1)];
  useEffect(() => {
    const timer = setInterval(() => setIdx(i => (i + 1) % allFrames.length), 80);
    return () => clearInterval(timer);
  }, [allFrames.length]);
  return (
    <img src={allFrames[idx]} alt="Boomerang" className="max-h-[60vh] rounded-2xl shadow-2xl" style={{ animation: 'resultEntry 0.3s ease-out' }} />
  );
};

};

export { ChallengeBadgePopup };
export default PocketBooth;
